"use client";

/**
 * The `address` field of any entity that carries the full CRM address set.
 *
 * `EnhancedGenericCreateForm` is the one form behind the create AND edit modals
 * for every CRM entity (`EntityCreateModals` / `EntityEditDeleteModals`), so
 * teaching the dispatcher about address here reaches Create Contact, Create
 * Company, Edit Contact and Edit Company at once — the four surfaces #743 lists
 * — without a per-entity opt-in that a fifth entity would forget.
 *
 * ELIGIBILITY is structural, not a hardcoded entity list: the renderer engages
 * only when the SAME config also declares `city`, `state`, `zip_code` and
 * `country`. A form with a lone `address` field has nowhere to put the parsed
 * parts, and filling a street into it while the rest of the place silently
 * vanished would be worse than the plain textarea it replaces.
 *
 * FOCUS: everything the field does while it helps is delegated to
 * `AddressAutocompleteInput`, which is written to keep the caret where the user
 * left it (`TYPING_FOCUS_STABILITY`). This file must not add a `disabled` or a
 * remount on top of it.
 */

import React from "react";

import { AddressAutocompleteInput } from "@/components/shared/address/AddressAutocompleteInput";
import { CRM_ADDRESS_FIELD_NAMES } from "@/lib/address/canonical";
import { FieldWrapper } from "./context";
import type { FieldRenderContext } from "./context";

/** The sibling columns a parsed address needs somewhere to land. */
const REQUIRED_SIBLING_FIELDS = ["city", "state", "zip_code", "country"] as const;

/**
 * Does this form have the whole address set?
 *
 * Read off the config rather than the entity name so a new entity with the same
 * five columns gets autocomplete for free, and an entity with only a free-text
 * `address` keeps its ordinary control.
 */
export function isFullCrmAddressForm(ctx: FieldRenderContext): boolean {
  const fields: unknown = ctx.config?.fields;
  if (!fields || typeof fields !== "object") return false;
  const declared = fields as Record<string, unknown>;
  return REQUIRED_SIBLING_FIELDS.every((name) => Boolean(declared[name]));
}

export const AddressFieldRenderer: React.FC<{ ctx: FieldRenderContext }> = ({ ctx }) => {
  const { fieldName, field, setValue, watchedValues, errors } = ctx;
  const hasError = !!errors[fieldName];
  const errorMessage = errors[fieldName]?.message as string | undefined;

  // Watched, not `register`ed: the value is also written by the picker, and a
  // controlled input re-renders IN PLACE — which is what keeps focus and
  // characters when suggestions arrive.
  const value = typeof watchedValues[fieldName] === "string" ? watchedValues[fieldName] : "";

  return (
    <FieldWrapper
      fieldName={fieldName}
      field={field}
      hasError={hasError}
      errorMessage={errorMessage}
    >
      <AddressAutocompleteInput
        id={fieldName}
        // The wrapper already renders the label; rendering a second one would
        // give the input two `<label for>` targets.
        label={null}
        value={value}
        placeholder={field.placeholder || "123 Main Street"}
        hasError={hasError}
        onValueChange={(next) =>
          setValue(fieldName, next, { shouldDirty: true, shouldValidate: false })
        }
        onAddressSelected={(fields) => {
          // The same five columns, through the same projection, that the
          // Meridian `lookup_address` tool writes. `address-parity.test.ts`
          // asserts the two agree.
          for (const column of CRM_ADDRESS_FIELD_NAMES) {
            setValue(column, fields[column], {
              shouldDirty: true,
              shouldValidate: true,
            });
          }
        }}
      />
    </FieldWrapper>
  );
};
